import type { ProjectSubsidyConfig } from 'features/master/api/types';
import type { BulkMortgageRuleInput } from 'features/notification-config/types/businessRule';
import type { PromptSectionCategory } from 'features/projects/lib/promptSectionCategories';

export const MORTGAGE_VERTICAL_ENABLED = 'MORTGAGE_VERTICAL_ENABLED';
export const MORTGAGE_AGENT_ENABLED = 'MORTGAGE_AGENT_ENABLED';
export const FINANCIAL_ADVISOR_AGENT_ENABLED = 'FINANCIAL_ADVISOR_AGENT_ENABLED';
export const LEASING_VERTICAL_ENABLED = 'LEASING_VERTICAL_ENABLED';
export const LEASING_AGENT_ENABLED = 'LEASING_AGENT_ENABLED';
export const MORTGAGE_PRE_EVALUATION_CONFIG = 'MORTGAGE_PRE_EVALUATION_CONFIG';

export const MORTGAGE_BOOLEAN_RULE_NAMES = [
  MORTGAGE_VERTICAL_ENABLED,
  MORTGAGE_AGENT_ENABLED,
  FINANCIAL_ADVISOR_AGENT_ENABLED,
  LEASING_VERTICAL_ENABLED,
  LEASING_AGENT_ENABLED,
] as const;

export const MORTGAGE_RULE_NAMES = [...MORTGAGE_BOOLEAN_RULE_NAMES, MORTGAGE_PRE_EVALUATION_CONFIG] as const;

export type MortgageBooleanRuleName = (typeof MORTGAGE_BOOLEAN_RULE_NAMES)[number];

/* The three the screen controls, in the order it lays them out: the product first, then the agents it
   makes room for. Leasing is not among them because it runs on masters without sub-projects. */
export const MORTGAGE_CONFIGURABLE_RULE_NAMES = [
  MORTGAGE_VERTICAL_ENABLED,
  FINANCIAL_ADVISOR_AGENT_ENABLED,
  MORTGAGE_AGENT_ENABLED,
] as const;

export type MortgageConfigurableRuleName = (typeof MORTGAGE_CONFIGURABLE_RULE_NAMES)[number];

/* The agents alone. The mode is operated for the whole master, so a per-sub-project column for it
   would report a state nobody sets one project at a time. */
export const MORTGAGE_AGENT_RULE_NAMES = [FINANCIAL_ADVISOR_AGENT_ENABLED, MORTGAGE_AGENT_ENABLED] as const;

/* A project runs one financing product. The backend resolves it the same way and logs a warning when
   both rules are on, so the screen never offers that combination. */
export type FinancingVertical = 'mortgage' | 'leasing';

/* What the screen is configuring. A master with sub-projects reads its rules on each sub-project and
   moves clients between agents; a master without them is financial-entity lead management, one prompt
   and no agent modes at all. */
export type MortgageTopology = 'subProjects' | 'leadManagement';

export interface MortgageRulesState {
  [MORTGAGE_VERTICAL_ENABLED]: boolean;
  [MORTGAGE_AGENT_ENABLED]: boolean;
  [FINANCIAL_ADVISOR_AGENT_ENABLED]: boolean;
  [LEASING_VERTICAL_ENABLED]: boolean;
  [LEASING_AGENT_ENABLED]: boolean;
}

export interface MortgagePreEvaluationConfig {
  entityCodes: string[];
  isMultiAgent: boolean;
  isDemo: boolean;
  sendsCapacityPdf: boolean;
}

export const EMPTY_MORTGAGE_RULES_STATE: MortgageRulesState = {
  [MORTGAGE_VERTICAL_ENABLED]: false,
  [MORTGAGE_AGENT_ENABLED]: false,
  [FINANCIAL_ADVISOR_AGENT_ENABLED]: false,
  [LEASING_VERTICAL_ENABLED]: false,
  [LEASING_AGENT_ENABLED]: false,
};

/* Mirrors the backend default: an absent `isMultiAgent` means the split is on, so a project with no
   configuration row runs multi-agent and the screen shows it that way. */
export const parsePreEvaluationConfig = (value: unknown): MortgagePreEvaluationConfig => {
  const raw = value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const codes = Array.isArray(raw.entityCodes) ? raw.entityCodes : [];

  return {
    entityCodes: codes.filter((code): code is string => typeof code === 'string'),
    isMultiAgent: raw.isMultiAgent !== false,
    isDemo: raw.isDemo === true,
    sendsCapacityPdf: raw.sendsCapacityPdf !== false,
  };
};

/* Mirrors the DS19 ceiling the backend falls back to when the sub-project sets none. */
export const DEFAULT_DS19_MAX_PROPERTY_VALUE_UF = 2600;

const positiveOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

/* The DS19 amount and ceiling; anything that is not a positive number reads as unset, as the agent reads it. */
export const readDs19Config = (
  config: ProjectSubsidyConfig | null | undefined
): { amountUF: number | null; maxPropertyValueUF: number | null } => ({
  amountUF: positiveOrNull(config?.ds19?.amountUF),
  maxPropertyValueUF: positiveOrNull(config?.ds19?.maxPropertyValueUF),
});

/* What gets written back: null when both are cleared. */
export const buildSubsidyConfigField = (
  amountUF: number | null,
  maxPropertyValueUF: number | null
): ProjectSubsidyConfig | null => {
  if (amountUF === null && maxPropertyValueUF === null) return null;
  return {
    ds19: {
      ...(amountUF !== null && { amountUF }),
      ...(maxPropertyValueUF !== null && { maxPropertyValueUF }),
    },
  };
};

/* Mirrors DS19_MAX_PROPERTY_VALUE_CEILING_UF in the backend, which rejects anything above it: a typo guard.
   It caps the DS19 amount as well, since the subsidy cannot exceed the most expensive unit. */
export const MAX_DS19_PROPERTY_VALUE_CEILING_UF = 4000;

/* Whole UF with up to two decimals, after a comma or a dot. */
const UF_INPUT_PATTERN = /^\d+([.,]\d{1,2})?$/;
/* Groups of three after a separator read as thousands, the way the cell shows them ("1.100 UF"). Parsed as a
   decimal they would store 1.1 UF for 1100, a valid amount nothing else would catch. */
const THOUSANDS_SEPARATOR_PATTERN = /^\d{1,3}([.,]\d{3})+$/;

/**
 * Reads a UF figure the operator typed: a number, or null for an empty field, or an error to show. `max` is the
 * field's typo guard.
 */
export const parseUFInput = (input: string, max: number): { valueUF: number | null } | { error: string } => {
  const trimmed = input.trim();
  if (trimmed === '') return { valueUF: null };
  if (THOUSANDS_SEPARATOR_PATTERN.test(trimmed)) {
    return { error: 'escríbelo sin separador de miles, por ejemplo 1100.' };
  }
  if (!UF_INPUT_PATTERN.test(trimmed)) return { error: 'ingresa un valor en UF, por ejemplo 600 o 600,5.' };
  const valueUF = Number(trimmed.replace(',', '.'));
  if (valueUF <= 0) return { error: 'ingresa un valor mayor a 0.' };
  if (valueUF > max) return { error: `no puede superar ${max} UF.` };
  return { valueUF };
};

/* Leasing wins when both are on, the same precedence the backend applies. */
export const resolveVertical = (rules: MortgageRulesState): FinancingVertical | null => {
  if (rules[LEASING_VERTICAL_ENABLED]) return 'leasing';
  if (rules[MORTGAGE_VERTICAL_ENABLED]) return 'mortgage';
  return null;
};

/* The label and description of every rule the screen names, wherever it names it. */
const SWITCH_COPY: Record<MortgageConfigurableRuleName, { label: string; description: string }> = {
  [MORTGAGE_VERTICAL_ENABLED]: {
    label: 'Modo hipotecario',
    description:
      'Habilita el producto hipotecario en el proyecto. Apagado, el proyecto atiende solo con el agente de ventas.',
  },
  [FINANCIAL_ADVISOR_AGENT_ENABLED]: {
    label: 'Asesor financiero',
    description: 'Estima capacidad de financiamiento y renta requerida antes de que haya una propiedad elegida.',
  },
  [MORTGAGE_AGENT_ENABLED]: {
    label: 'Agente brokerage',
    description:
      /* One sentence per line: two separate things are being said, and read as a block they blur. */
      'Simula el crédito, revisa políticas de las entidades y gestiona los documentos.\n' +
      'Se activa por lo general en proyectos con entrega inmediata o hasta tres meses.',
  },
};

export const ruleLabel = (name: MortgageConfigurableRuleName): string => SWITCH_COPY[name].label;

export interface ProjectRulesSnapshot {
  id: number;
  rules: MortgageRulesState;
  isMultiAgent: boolean;
}

/** Whether every selected project runs the split, which is what makes the advisor meaningful there. */
export const selectionRunsSplit = (selected: ProjectRulesSnapshot[]): boolean =>
  selected.length > 0 && selected.every((project) => project.isMultiAgent);

/*
 * The agents hang off the mode: turning it off takes them with it. Each agent is switched on and off
 * on its own.
 */
export const buildBulkRules = (
  name: MortgageConfigurableRuleName,
  value: boolean
): { name: MortgageConfigurableRuleName; value: boolean }[] => {
  if (name === MORTGAGE_VERTICAL_ENABLED && !value) {
    return [
      { name: MORTGAGE_VERTICAL_ENABLED, value: false },
      { name: MORTGAGE_AGENT_ENABLED, value: false },
      { name: FINANCIAL_ADVISOR_AGENT_ENABLED, value: false },
    ];
  }

  return [{ name, value }];
};

export interface MortgageBulkAction {
  name: MortgageConfigurableRuleName;
  label: string;
  description: string;
  /*
   * Which directions the selection can actually move. Offering both on a set that already sits on one
   * side turns half the buttons into no-ops, so only the direction with something to change is shown —
   * and both only when the selection is split on that rule. With nothing selected both are shown, for
   * the screen to dim: what can be done here does not depend on what happens to be selected.
   */
  canActivate: boolean;
  canDeactivate: boolean;
}

/** How many of the selected projects still have the mode off, which is what keeps the agents away. */
export const countWithoutMode = (selected: ProjectRulesSnapshot[]): number =>
  selected.filter((project) => !project.rules[MORTGAGE_VERTICAL_ENABLED]).length;

/*
 * The projects a change would actually move. The rest would take a version bump and an audit entry for
 * a value they already hold, which is noise in a table people read to know what changed.
 */
export const idsToChange = (
  name: MortgageConfigurableRuleName,
  value: boolean,
  selected: ProjectRulesSnapshot[]
): number[] => selected.filter((project) => project.rules[name] !== value).map((project) => project.id);

const buildAction = (name: MortgageConfigurableRuleName, selected: ProjectRulesSnapshot[]): MortgageBulkAction => ({
  name,
  ...SWITCH_COPY[name],
  canActivate: selected.length === 0 || selected.some((project) => !project.rules[name]),
  canDeactivate: selected.length === 0 || selected.some((project) => project.rules[name]),
});

/*
 * What a selection can be moved as a block. The mode is always offered — it is the way in and the way
 * out. The agents hang off it: if any selected project has the mode off, its agents would not reach a
 * conversation, so they are not offered for the set.
 */
export const resolveBulkActions = (selected: ProjectRulesSnapshot[]): MortgageBulkAction[] => {
  const mode = buildAction(MORTGAGE_VERTICAL_ENABLED, selected);
  if (selected.length > 0 && countWithoutMode(selected) > 0) return [mode];

  const runsSplit = selected.length === 0 || selectionRunsSplit(selected);
  const brokerage = buildAction(MORTGAGE_AGENT_ENABLED, selected);

  return runsSplit ? [mode, buildAction(FINANCIAL_ADVISOR_AGENT_ENABLED, selected), brokerage] : [mode, brokerage];
};

export interface MortgageChangeCopy {
  title: string;
  description: string;
  confirmLabel: string;
}

/*
 * The mark lives on the master project (`isFinancialEntity`), not in a rule: it decides the shape of every
 * conversation in the company, so the agent reads it from the project it already has loaded.
 */
export const describeFinancialEntityChange = (isFinancialEntity: boolean, projectName: string): MortgageChangeCopy =>
  isFinancialEntity
    ? {
        title: `Marcar ${projectName} como entidad financiera`,
        description:
          'Sus clientes pasan a atenderse como Lead Management: un solo agente, sin cambios de modo, y las ' +
          'estimaciones usan la configuración de sus entidades. Si tiene subproyectos, se leen como productos de ' +
          'la entidad y no como proyectos inmobiliarios.',
        confirmLabel: 'Marcar',
      }
    : {
        title: `Quitar la marca de entidad financiera a ${projectName}`,
        description:
          'Sus clientes pasan a atenderse como inmobiliaria: los que estén en un subproyecto con agentes ' +
          'financieros activos podrán cambiar de modo, y las estimaciones usan los valores por defecto.',
        confirmLabel: 'Quitar marca',
      };

/*
 * What the operator is told before a write that changes which agent answers. The rules decide the
 * tools; the prompt decides how they are used, and nothing here can check that the two agree — so the
 * copy says out loud what has to be verified by hand.
 */
export const describeLeadManagementChange = (
  selection: LeadManagementSelection,
  projectName: string
): MortgageChangeCopy => {
  const vertical = selection.enabled ? selection.vertical : null;

  if (vertical === 'mortgage') {
    return {
      title: `Activar financiamiento hipotecario en ${projectName}`,
      description:
        'El agente pasará a hacerse cargo de las consultas de financiamiento: simulación de crédito, políticas de ' +
        'las entidades y gestión de documentos. Revisa que el prompt del proyecto esté alineado con ese ' +
        'comportamiento antes de continuar.',
      confirmLabel: 'Activar',
    };
  }

  if (vertical === 'leasing') {
    return {
      title: `Activar leasing habitacional en ${projectName}`,
      description:
        'El agente pasará a hacerse cargo de las consultas de leasing: simulación, políticas de las entidades y ' +
        'estimación de capacidad. Revisa que el prompt del proyecto esté alineado con ese comportamiento antes de ' +
        'continuar.',
      confirmLabel: 'Activar',
    };
  }

  return {
    title: `Desactivar el financiamiento en ${projectName}`,
    description:
      'Las herramientas de simulación y evaluación dejan de estar disponibles para el agente. Confirma que el ' +
      'prompt cubre las consultas de financiamiento antes de continuar.',
    confirmLabel: 'Desactivar',
  };
};

export interface LeadManagementSelection {
  enabled: boolean;
  vertical: FinancingVertical | null;
}

export const resolveLeadManagementSelection = (rules: MortgageRulesState): LeadManagementSelection => {
  const vertical = resolveVertical(rules);
  return { enabled: vertical !== null, vertical };
};

/*
 * Lead management has one agent, so its rules never live apart: the switch and the vertical write all
 * five at once, and switching vertical turns the other one off in the same request rather than leaving
 * both on — a combination the backend only warns about.
 */
export const buildLeadManagementRules = (selection: LeadManagementSelection): BulkMortgageRuleInput[] => {
  const vertical = selection.enabled ? selection.vertical : null;

  return [
    { name: MORTGAGE_VERTICAL_ENABLED, value: vertical === 'mortgage' },
    { name: MORTGAGE_AGENT_ENABLED, value: vertical === 'mortgage' },
    { name: FINANCIAL_ADVISOR_AGENT_ENABLED, value: vertical === 'mortgage' },
    { name: LEASING_VERTICAL_ENABLED, value: vertical === 'leasing' },
    { name: LEASING_AGENT_ENABLED, value: vertical === 'leasing' },
  ];
};

const BULK_COPY: Record<MortgageConfigurableRuleName, { on: string; off: string }> = {
  [MORTGAGE_VERTICAL_ENABLED]: {
    on:
      'Los subproyectos seleccionados pasan a operar el producto hipotecario. Elige después qué agentes se hacen ' +
      'cargo del financiamiento.',
    off:
      'Los subproyectos seleccionados vuelven a atender solo con el agente de ventas. Los clientes que estén con un ' +
      'agente de financiamiento vuelven a ventas en su próximo mensaje. Confirma que los prompts de venta cubren ' +
      'las consultas de financiamiento antes de continuar.',
  },
  [MORTGAGE_AGENT_ENABLED]: {
    on:
      'El agente brokerage pasará a hacerse cargo de las consultas de financiamiento: simulación de crédito, ' +
      'políticas de las entidades y gestión de documentos. Revisa que los prompts de esos proyectos estén alineados ' +
      'con ese comportamiento antes de continuar.',
    off:
      'Los clientes que hoy están con el agente brokerage vuelven a ventas en su próximo mensaje y las ' +
      'herramientas de simulación dejan de estar disponibles.',
  },
  [FINANCIAL_ADVISOR_AGENT_ENABLED]: {
    on:
      'El asesor financiero pasará a estimar capacidad de financiamiento y renta requerida antes de que el cliente ' +
      'elija una propiedad. Revisa que los prompts de esos proyectos contemplen esa derivación.',
    off:
      'El asesor deja de estar disponible y sus estimaciones salen del repertorio del agente. Los clientes que estén ' +
      'en ese modo vuelven a ventas en su próximo mensaje.',
  },
};

export const describeBulkPreEvaluation = (count: number): MortgageChangeCopy => ({
  title: `Aplicar la configuración de pre-evaluación en ${count} ${count === 1 ? 'subproyecto' : 'subproyectos'}`,
  description:
    'La configuración reemplaza por completo la que cada subproyecto tenga hoy, incluidas sus entidades ' +
    'financieras. Revisa la selección antes de continuar.',
  confirmLabel: 'Aplicar',
});

export const describeBulkChange = (
  name: MortgageConfigurableRuleName,
  value: boolean,
  count: number
): MortgageChangeCopy => {
  const scope = `${count} ${count === 1 ? 'subproyecto' : 'subproyectos'}`;

  return {
    title: `${value ? 'Activar' : 'Desactivar'} ${ruleLabel(name).toLowerCase()} en ${scope}`,
    description: value ? BULK_COPY[name].on : BULK_COPY[name].off,
    confirmLabel: value ? 'Activar' : 'Desactivar',
  };
};

/*
 * The per-project agent prompt fields hold a `<GLOBALSECTION:name>` tag rather than a prompt: the
 * text lives in the global section and the backend resolves the tag while scanning the base prompt.
 * An empty field means the agent runs the system default, which is why the picker offers it as an
 * option rather than as an absence.
 */
const SECTION_TAG = /^\s*<GLOBALSECTION:([\w-]+)>\s*$/;

/* The picker needs a value for "runs the default", and an empty string is not one: the select
   primitive reserves it for "nothing chosen". */
export const DEFAULT_PROMPT_OPTION = '__default__';

/** Section name held by a project's agent prompt field, or '' when it runs the default. */
export const readPromptSectionName = (field: string | null | undefined): string => {
  const match = field?.match(SECTION_TAG);
  return match ? match[1] : DEFAULT_PROMPT_OPTION;
};

/** What gets written back: the tag for a chosen section, or null to fall back to the default. */
export const buildPromptSectionField = (sectionName: string): string | null =>
  sectionName === DEFAULT_PROMPT_OPTION ? null : `<GLOBALSECTION:${sectionName}>`;

export interface AgentPromptField {
  key: 'financialAdvisorAgentPrompt' | 'mortgageAgentPrompt';
  /* The rule that turns this agent on. The table puts the prompt next to it, because a prompt on an
     agent nobody reaches is not a setting anyone needs to see on its own. */
  rule: MortgageConfigurableRuleName;
  label: string;
  shortLabel: string;
  description: string;
  /* The global-section category that marks a section as a prompt for this agent. */
  sectionCategory: Extract<PromptSectionCategory, 'advisor_prompt' | 'brokerage_prompt'>;
}

/* Laid out in the order the agents appear in the table: orientation first, then evaluation. */
export const AGENT_PROMPT_FIELDS: AgentPromptField[] = [
  {
    key: 'financialAdvisorAgentPrompt',
    rule: FINANCIAL_ADVISOR_AGENT_ENABLED,
    label: 'Prompt del orientador financiero',
    shortLabel: 'Su prompt',
    description:
      'Qué versión del prompt usa el orientador en estos subproyectos. Por defecto corre la sección global del sistema.',
    sectionCategory: 'advisor_prompt',
  },
  {
    key: 'mortgageAgentPrompt',
    rule: MORTGAGE_AGENT_ENABLED,
    label: 'Prompt del agente hipotecario',
    shortLabel: 'Su prompt',
    description: 'Mismo criterio para el agente hipotecario, que hoy se configura escribiendo el tag en la base.',
    sectionCategory: 'brokerage_prompt',
  },
];

/** The prompt column that follows a rule column, when that rule turns on an agent that has one. */
export const promptFieldForRule = (rule: MortgageConfigurableRuleName): AgentPromptField | undefined =>
  AGENT_PROMPT_FIELDS.find((field) => field.rule === rule);
